import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "..";
import Swal from "sweetalert2";
import { Icon } from '@iconify/react/dist/iconify.js';

const CustomerTable = ({
  data = [],
  currentPage,
  totalPages,
  onPageChange,
  limit,
  onLimitChange,
  searchQuery,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState(null);
  const [expandedMobileCards, setExpandedMobileCards] = useState({});
  // Pure-React action menu (no Bootstrap JS / Popper). Bootstrap's dropdown
  // moves the menu node in the DOM for positioning; when the table then
  // re-rendered React lost track of that node -> "insertBefore" crash.
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const closeOnOutside = (e) => {
      if (
        !e.target.closest(
          ".action-dropdown, .customer-mobile-action-dropdown"
        )
      ) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", closeOnOutside);
    return () => document.removeEventListener("click", closeOnOutside);
  }, [openMenuId]);

  const toggleMobileDetails = (customerId) => {
    setExpandedMobileCards((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const handleDelete = async (id, customerName) => {
    try {
      const token = localStorage.getItem("tokenID");

      const confirm = await Swal.fire({
        icon: "warning",
        title: "Delete Customer?",
        html: `Are you sure you want to delete <strong>${customerName}</strong>?<br/>This action cannot be undone.`,
        showCancelButton: true,
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#111827",
        cancelButtonColor: "#6c757d",
        reverseButtons: true,
      });

      if (!confirm.isConfirmed) return;

      setDeletingId(id);

      Swal.fire({
        title: "Deleting...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const response = await fetch(`${API_URL}/api/customer/delete/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, contactId: id }),
      });

      const result = await response.json().catch(() => ({}));
      Swal.close();

      if (response.ok) {
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: `${customerName} has been removed successfully.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#111827",
        });

        window.location.reload();
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: result.message || "Failed to delete customer.",
        confirmButtonText: "OK",
        confirmButtonColor: "#111827",
      });
    } catch (error) {
      console.error("Delete error:", error);
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete customer. Please try again later.",
        confirmButtonText: "OK",
        confirmButtonColor: "#111827",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "N/A";
    return phone;
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "N/A";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // CustomerPage already filters + paginates (client-side over the full list),
  // so `data` is exactly the rows to show for this page. Don't re-filter here.
  const filteredData = data;

  const renderActionsDropdown = (customer, variant = "desktop") => {
    const menuKey = `${variant}-${customer.id}`;
    const isOpen = openMenuId === menuKey;
    return (
      <div className={`dropdown ${variant === "mobile" ? "customer-mobile-action-dropdown" : "action-dropdown"}`}>
        <button
          className={variant === "mobile" ? "customer-mobile-action-toggle dropdown-toggle" : "btn dropdown-toggle"}
          type="button"
          aria-expanded={isOpen}
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenuId(isOpen ? null : menuKey);
          }}
        >
          {variant === "mobile" ? (
            "Actions"
          ) : (
            <Icon icon="mdi:dots-vertical" style={{ fontSize: "18px" }} />
          )}
        </button>

        <ul className={`dropdown-menu dropdown-menu-end${isOpen ? " show" : ""}`}>
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenMenuId(null);
                navigate(`/customer/view/${customer.id}`);
              }}
            >
              <Icon icon="mdi:eye" className="me-2" style={{ fontSize: "16px" }} />
              View Details
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenMenuId(null);
                navigate(`/customer/edit/${customer.id}`);
              }}
            >
              <Icon icon="mdi:pencil" className="me-2" style={{ fontSize: "16px" }} />
              Edit Customer
            </button>
          </li>
          <li>
            <button
              className="dropdown-item text-danger"
              onClick={() => {
                setOpenMenuId(null);
                handleDelete(customer.id, customer.name);
              }}
              disabled={deletingId === customer.id}
            >
              {deletingId === customer.id ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" />
              ) : (
                <Icon icon="mdi:delete" className="me-2" style={{ fontSize: "16px" }} />
              )}
              Delete Customer
            </button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="customer-table-page">
      <style>
        {`
          .customer-table-page {
            --customer-primary: #111827;
            --customer-muted: #6b7280;
            --customer-border: #e5e7eb;
            --customer-soft: #f9fafb;
            --customer-danger: #b91c1c;
          }

          .customer-card-shell {
            border: 1px solid var(--customer-border);
            border-radius: 18px;
            overflow: visible;
            background: #fff;
            box-shadow: 0 10px 28px rgba(15, 23, 42, 0.05);
          }

          .customer-table-header {
            padding: 20px 24px;
            border-bottom: 1px solid var(--customer-border);
            background: #fff;
          }

          .customer-header-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(280px, 1.5fr) auto;
            gap: 16px;
            align-items: center;
          }

          .customer-title {
            margin: 0;
            color: var(--customer-primary);
            font-size: 18px;
            font-weight: 900;
            letter-spacing: -0.02em;
            display: flex;
            align-items: center;
            gap: 9px;
          }

          .customer-title-icon {
            width: 36px;
            height: 36px;
            border-radius: 12px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f3f4f6;
            border: 1px solid var(--customer-border);
            color: var(--customer-primary);
          }

          .customer-search-wrap {
            position: relative;
          }

          .customer-search-icon {
            position: absolute;
            left: 13px;
            top: 50%;
            transform: translateY(-50%);
            color: #9ca3af;
            font-size: 19px;
            z-index: 2;
          }

          .customer-search-input {
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid var(--customer-border);
            padding-left: 42px;
            color: var(--customer-primary);
            box-shadow: none;
          }

          .customer-search-input:focus {
            border-color: var(--customer-primary);
            box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
          }

          .rows-dropdown {
            position: relative;
            z-index: 30;
          }

          .rows-dropdown-toggle {
            min-height: 44px;
            border-radius: 12px;
            border: 1px solid var(--customer-border);
            background: #fff;
            color: var(--customer-primary);
            padding: 9px 14px;
            font-size: 14px;
            font-weight: 850;
          }

          .rows-dropdown .dropdown-menu {
            border-radius: 14px;
            border: 1px solid var(--customer-border);
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
            padding: 6px;
            z-index: 99999;
          }

          .rows-dropdown .dropdown-item {
            border-radius: 10px;
            font-size: 13px;
            font-weight: 750;
            padding: 9px 12px;
          }

          .customer-desktop-table {
            display: block;
          }

          .customer-mobile-list {
            display: none;
          }

          .customer-table-wrap {
            width: 100%;
            overflow: visible !important;
          }

          .customer-table-wrap,
          .customer-table,
          .customer-table tbody,
          .customer-table tr,
          .customer-table td {
            overflow: visible !important;
          }

          .customer-table {
            margin-bottom: 0;
            min-width: 760px;
          }

          .customer-table thead th {
            background: #f9fafb;
            color: #374151;
            font-weight: 900;
            padding: 15px 12px;
            border-bottom: 1px solid var(--customer-border);
            font-size: 12px;
            white-space: nowrap;
            text-transform: uppercase;
            letter-spacing: 0.03em;
          }

          .customer-table tbody td {
            padding: 13px 12px;
            vertical-align: middle;
            border-bottom: 1px solid #f1f5f9;
            color: #4b5563;
            font-size: 13px;
            white-space: nowrap;
          }

          .customer-table tbody tr:hover {
            background: #f9fafb;
          }

          .customer-table tbody tr,
          .customer-table tbody td {
            position: relative;
            z-index: 1;
          }

          .customer-table tbody tr:has(.dropdown-menu.show),
          .customer-table tbody td:has(.dropdown-menu.show) {
            z-index: 1000000 !important;
          }

          .customer-table tbody tr:has(.dropdown-menu.show) ~ tr,
          .customer-table tbody tr:has(.dropdown-menu.show) ~ tr td,
          .customer-table tbody tr:has(.dropdown-menu.show) ~ tr .action-dropdown {
            z-index: 1 !important;
          }

          .action-dropdown {
            position: relative;
            z-index: 1;
            display: inline-block;
          }

          .action-dropdown:has(.dropdown-menu.show) {
            z-index: 1000000 !important;
          }

          .action-dropdown .dropdown-toggle {
            background: #fff;
            color: var(--customer-primary);
            border: 1px solid var(--customer-border);
            border-radius: 10px;
            padding: 8px 11px;
            transition: all 0.15s ease;
          }

          .action-dropdown .dropdown-toggle:hover {
            background: #f9fafb;
            border-color: var(--customer-primary);
          }

          .action-dropdown .dropdown-menu {
            border-radius: 14px;
            border: 1px solid var(--customer-border);
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
            padding: 6px;
            z-index: 1000001 !important;
          }

          .action-dropdown .dropdown-menu.show {
            display: block;
            position: absolute !important;
            z-index: 1000001 !important;
            inset: auto 0 auto auto !important;
            transform: translate3d(0, 38px, 0) !important;
            isolation: isolate;
          }

          .action-dropdown .dropdown-menu.show * {
            position: relative;
            z-index: 1000002;
          }

          .dropdown-item {
            padding: 9px 12px;
            cursor: pointer;
            font-size: 13px;
            font-weight: 750;
            border-radius: 10px;
            display: flex;
            align-items: center;
          }

          .dropdown-item:hover {
            background: #f9fafb;
          }

          .badge-customer {
            background: #f3f4f6;
            color: var(--customer-primary);
            padding: 5px 8px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 900;
            border: 1px solid var(--customer-border);
          }

          .customer-mobile-card {
            border: 1px solid var(--customer-border);
            border-radius: 16px;
            background: #fff;
            padding: 15px;
            box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
          }

          .customer-mobile-card + .customer-mobile-card {
            margin-top: 12px;
          }

          .customer-mobile-top {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 12px;
          }

          .customer-mobile-main {
            min-width: 0;
            flex: 1 1 auto;
          }

          .customer-mobile-name {
            color: var(--customer-primary);
            font-size: 15px;
            font-weight: 900;
            word-break: break-word;
          }

          .customer-mobile-phone {
            margin-top: 5px;
            color: var(--customer-muted);
            font-size: 13px;
            font-weight: 700;
            word-break: break-word;
            display: flex;
            align-items: center;
            gap: 5px;
          }

          .customer-mobile-side {
            display: inline-flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
            flex: 0 0 auto;
          }

          .customer-index-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 34px;
            height: 30px;
            border-radius: 999px;
            background: #f9fafb;
            border: 1px solid var(--customer-border);
            color: var(--customer-primary);
            font-size: 12px;
            font-weight: 900;
          }

          .customer-detail-toggle {
            width: 30px;
            height: 30px;
            border-radius: 999px;
            border: 1px solid var(--customer-border);
            background: #f9fafb;
            color: var(--customer-primary);
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            transition: all 0.18s ease;
            padding: 0;
          }

          .customer-detail-toggle:hover {
            background: #f3f4f6;
            border-color: #d1d5db;
          }

          .customer-detail-toggle.is-open {
            background: var(--customer-primary);
            color: #fff;
            border-color: var(--customer-primary);
          }

          .customer-detail-toggle svg {
            transition: transform 0.18s ease;
          }

          .customer-detail-toggle.is-open svg {
            transform: rotate(180deg);
          }

          .customer-mobile-details {
            display: none;
            padding: 12px 0;
            border-top: 1px solid var(--customer-border);
            border-bottom: 1px solid var(--customer-border);
            animation: customerDetailsSlide 0.18s ease-out;
          }

          .customer-mobile-details.open {
            display: block;
          }

          .customer-mobile-grid {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }

          @keyframes customerDetailsSlide {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .customer-mobile-label {
            color: var(--customer-muted);
            font-size: 11px;
            font-weight: 850;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-bottom: 3px;
          }

          .customer-mobile-value {
            color: var(--customer-primary);
            font-size: 13px;
            font-weight: 800;
            word-break: break-word;
          }

          .customer-mobile-footer {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 12px;
          }

          .customer-mobile-action-dropdown {
            position: relative;
            flex: 0 0 auto;
          }

          .customer-mobile-action-toggle {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 40px;
            padding: 9px 13px;
            border-radius: 12px;
            border: 1px solid var(--customer-primary);
            background: var(--customer-primary);
            color: #fff;
            font-size: 13px;
            font-weight: 900;
            box-shadow: 0 8px 18px rgba(17, 24, 39, 0.12);
          }

          .customer-mobile-action-dropdown .dropdown-menu {
            z-index: 99999;
            min-width: 215px;
            padding: 6px;
            border: 1px solid var(--customer-border);
            border-radius: 14px;
            box-shadow: 0 18px 38px rgba(15, 23, 42, 0.16);
          }

          .customer-mobile-action-dropdown .dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            border-radius: 10px;
            padding: 10px 12px;
            color: var(--customer-primary);
            font-size: 13px;
            font-weight: 800;
          }

          .customer-pagination {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            padding: 16px;
            border-top: 1px solid var(--customer-border);
          }

          .customer-page-controls {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .customer-page-btn {
            border-radius: 10px;
            padding: 7px 12px;
            font-size: 13px;
            font-weight: 850;
          }

          .customer-page-pill {
            padding: 7px 12px;
            background: #f9fafb;
            border: 1px solid var(--customer-border);
            border-radius: 10px;
            font-size: 13px;
            color: var(--customer-primary);
          }

          @media (max-width: 767.98px) {
            .customer-card-shell {
              border-radius: 14px;
              box-shadow: none;
            }

            .customer-table-header {
              padding: 16px;
            }

            .customer-header-grid {
              grid-template-columns: 1fr;
              gap: 12px;
            }

            .rows-dropdown-toggle {
              width: 100%;
              justify-content: center;
            }

            .customer-desktop-table {
              display: none;
            }

            .customer-mobile-list {
              display: block;
              padding: 14px;
            }

            .customer-mobile-grid {
              grid-template-columns: 1fr;
            }

            .customer-mobile-top {
              gap: 10px;
            }

            .customer-mobile-side {
              align-items: flex-start;
            }

            .customer-pagination {
              flex-direction: column;
              align-items: stretch;
              padding: 14px;
            }

            .customer-page-controls {
              display: grid;
              grid-template-columns: 1fr;
              gap: 8px;
            }

            .customer-page-controls .btn,
            .customer-page-btn,
            .customer-page-pill {
              width: 100%;
              text-align: center;
              justify-content: center;
            }
          }
        `}
      </style>

      <div className="customer-card-shell">
        <div className="customer-table-header">
          <div className="customer-header-grid">
            <h5 className="customer-title">
              <span className="customer-title-icon">
                <Icon icon="mdi:users" style={{ fontSize: 20 }} />
              </span>
              Customers List
            </h5>

            <div className="customer-search-wrap">
              <Icon icon="mdi:magnify" className="customer-search-icon" />
              <input
                type="text"
                className="form-control customer-search-input"
                placeholder="Search by name, phone, or email..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>

            <div className="dropdown rows-dropdown">
              <button
                className="rows-dropdown-toggle dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                data-bs-boundary="viewport"
                data-bs-display="dynamic"
                aria-expanded="false"
              >
                <Icon icon="mdi:table" className="me-1" />
                Rows: {limit}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                {[10, 25, 50, 100, 300].map((value) => (
                  <li key={value}>
                    <button
                      className="dropdown-item"
                      onClick={() => onLimitChange(value)}
                      style={{ cursor: "pointer" }}
                    >
                      {value} rows
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="customer-desktop-table">
            <div className="customer-table-wrap">
              <table className="table customer-table">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>#</th>
                    <th style={{ width: "30%" }}>Customer Name</th>
                    <th style={{ width: "20%" }}>Phone</th>
                    <th style={{ width: "30%" }}>Email</th>
                    <th style={{ width: "15%" }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((customer, index) => (
                      <tr key={customer.id}>
                        <td>
                          <span className="badge-customer">
                            {(currentPage - 1) * limit + index + 1}
                          </span>
                        </td>
                        <td>
                          <div className="fw-semibold" style={{ color: "#111827" }}>
                            {truncateText(customer.name, 25)}
                          </div>
                        </td>
                        <td>
                          <Icon icon="mdi:phone" className="me-1" style={{ fontSize: "14px", color: "#6b7280" }} />
                          {formatPhoneNumber(customer.phone)}
                        </td>
                        <td>
                          <Icon icon="mdi:email" className="me-1" style={{ fontSize: "14px", color: "#6b7280", alignSelf: 'center' }} />
                          {customer.email || "N/A"}
                        </td>
                        <td className="text-center">
                          {renderActionsDropdown(customer, "desktop")}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <Icon icon="mdi:account-search" style={{ fontSize: "48px", color: "#ccc" }} />
                        {searchQuery ? (
                          <>
                            <p className="mt-2 mb-1 text-muted">
                              No contacts match "<strong>{searchQuery}</strong>"
                            </p>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => onSearchChange("")}
                            >
                              Clear search
                            </button>
                          </>
                        ) : (
                          <p className="mt-2 mb-0 text-muted">No customers found</p>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="customer-mobile-list">
            {filteredData.length > 0 ? (
              filteredData.map((customer, index) => {
                const rowNumber = (currentPage - 1) * limit + index + 1;

                return (
                  <div className="customer-mobile-card" key={customer.id}>
                    <div className="customer-mobile-top">
                      <div className="customer-mobile-main">
                        <div className="customer-mobile-name">{truncateText(customer.name, 40)}</div>
                        <div className="customer-mobile-phone">
                          <Icon icon="mdi:phone" style={{ fontSize: 14 }} />
                          {formatPhoneNumber(customer.phone)}
                        </div>
                      </div>

                      <div className="customer-mobile-side">
                        <span className="customer-index-badge">#{rowNumber}</span>
                        <button
                          type="button"
                          className={`customer-detail-toggle ${expandedMobileCards[customer.id] ? 'is-open' : ''}`}
                          onClick={() => toggleMobileDetails(customer.id)}
                          aria-expanded={!!expandedMobileCards[customer.id]}
                          aria-label={expandedMobileCards[customer.id] ? 'Hide customer details' : 'Show customer details'}
                        >
                          <Icon icon="mdi:chevron-down" style={{ fontSize: 20 }} />
                        </button>
                      </div>
                    </div>

                    <div className={`customer-mobile-details ${expandedMobileCards[customer.id] ? 'open' : ''}`}>
                      <div className="customer-mobile-grid">
                        <div>
                          <div className="customer-mobile-label">Email</div>
                          <div className="customer-mobile-value">{customer.email || "N/A"}</div>
                        </div>

                        <div>
                          <div className="customer-mobile-label">Phone</div>
                          <div className="customer-mobile-value">{formatPhoneNumber(customer.phone)}</div>
                        </div>

                        <div>
                          <div className="customer-mobile-label">Customer ID</div>
                          <div className="customer-mobile-value">{customer.id}</div>
                        </div>

                        <div>
                          <div className="customer-mobile-label">Row</div>
                          <div className="customer-mobile-value">#{rowNumber}</div>
                        </div>
                      </div>
                    </div>

                    <div className="customer-mobile-footer">
                      {renderActionsDropdown(customer, "mobile")}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-5">
                <Icon icon="mdi:account-search" style={{ fontSize: "48px", color: "#ccc" }} />
                <p className="mt-2 mb-0 text-muted">No customers found matching your search</p>
                <small className="text-muted">Try adjusting your search criteria</small>
              </div>
            )}
          </div>

          {filteredData.length > 0 && (
            <div className="customer-pagination">
              <div className="text-muted small">
                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, filteredData.length)} of {filteredData.length} entries
              </div>

              <div className="customer-page-controls">
                <button
                  className="btn btn-outline-secondary btn-sm customer-page-btn"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <Icon icon="mdi:chevron-left" />
                  Previous
                </button>

                <div className="customer-page-pill">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </div>

                <button
                  className="btn btn-outline-secondary btn-sm customer-page-btn"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <Icon icon="mdi:chevron-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerTable;
